import { Component } from '@theme/component';

const STAR_FULL =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M12 17.77L18.18 21.5L16.54 14.47L22 9.74L14.81 9.13L12 2.5L9.19 9.13L2 9.74L7.46 14.47L5.82 21.5L12 17.77Z"/></svg>';
const STAR_HALF =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="jmr-half"><stop offset="50%" stop-color="currentColor"/><stop offset="50%" stop-color="currentColor" stop-opacity="0.25"/></linearGradient></defs><path d="M12 17.77L18.18 21.5L16.54 14.47L22 9.74L14.81 9.13L12 2.5L9.19 9.13L2 9.74L7.46 14.47L5.82 21.5L12 17.77Z" fill="url(#jmr-half)"/></svg>';
const STAR_EMPTY =
  '<svg viewBox="0 0 24 24" fill="currentColor" opacity="0.25" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M12 17.77L18.18 21.5L16.54 14.47L22 9.74L14.81 9.13L12 2.5L9.19 9.13L2 9.74L7.46 14.47L5.82 21.5L12 17.77Z"/></svg>';

const VERIFIED_DOT =
  '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.0002 1.20001C5.05085 1.20001 4.12282 1.48153 3.33346 2.00896C2.5441 2.53639 1.92888 3.28605 1.56558 4.16313C1.20228 5.04022 1.10722 6.00534 1.29243 6.93644C1.47764 7.86755 1.93479 8.72283 2.60608 9.39412C3.27738 10.0654 4.13265 10.5226 5.06376 10.7078C5.99487 10.893 6.95999 10.7979 7.83708 10.4346C8.71416 10.0713 9.46382 9.4561 9.99125 8.66675C10.5187 7.87739 10.8002 6.94936 10.8002 6.00001C10.8002 4.72697 10.2945 3.50607 9.39431 2.6059C8.49413 1.70572 7.27324 1.20001 6.0002 1.20001ZM5.31448 7.91658L3.6002 6.2023L4.14534 5.65715L5.31448 6.8263L7.85505 4.28573L8.40225 4.8295L5.31448 7.91658Z" fill="#51B92C"/></svg>';

/** Bodies longer than this many characters collapse behind the read-more toggle. */
const READ_MORE_THRESHOLD = 50;

/**
 * Reviews come from the judgme-proxy Cloudflare Worker (/judgme-proxy in the repo). It holds
 * the private API token, so per_page and has_photo filter server-side and PAGE_SIZE is honoured verbatim.
 */
const PAGE_SIZE = 12;

/**
 * Judge.me review grid: tabs (all / with photo / newest), numbered pagination, a featured
 * review that falls back to the first fetched one, and per-card read-more toggles.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} grid
 * @property {HTMLElement} pagination
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement} [photoCount]
 * @property {HTMLElement} [featured]
 * @property {HTMLElement} [featuredQuote]
 * @property {HTMLElement} [featuredAuthor]
 *
 * @extends {Component<Refs>}
 */
class JudgmeReviewsComponent extends Component {
  requiredRefs = ['grid', 'pagination'];

  /** @type {Array<Record<string, any>>} */
  #reviews = [];
  /** @type {'all' | 'photos' | 'newest'} */
  #tab = 'all';
  #i18n = /** @type {Record<string, string>} */ ({});
  #page = 1;
  #totalPages = 1;
  /** Size of the unfiltered corpus the proxy serves (product + shop). */
  #allCount = 0;
  #withPhotoCount = 0;
  /** Aborts the in-flight page request when a newer one starts (latest request wins). */
  /** @type {AbortController | null} */
  #request = null;
  /** Page the error state's retry button loads again. */
  #failedPage = 1;
  #featuredFilled = false;

  get #proxyUrl() {
    return (this.dataset.proxyUrl || '').replace(/\/$/, '');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#i18n = this.#readI18n();
    this.#fetchCounts();
    this.#fetchPage(1);
  }

  /**
   * @param {Event} event - Click on a tab button.
   */
  selectTab(event) {
    const btn = event.target;
    if (!(btn instanceof HTMLElement)) return;

    for (const tab of this.refs.tabs ?? []) {
      const active = tab === btn;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
    }

    const tab = /** @type {'all' | 'photos' | 'newest'} */ (btn.dataset.tab);
    if (tab === this.#tab) return;
    this.#tab = tab;
    // Resize the pager before the fetch returns so the new tab shows its page count at once.
    this.#page = 1;
    this.#totalPages = Math.max(1, Math.ceil(this.#corpusSize() / PAGE_SIZE) || 1);
    this.#renderPagination();
    this.#fetchPage(1);
  }

  /**
   * @param {Event} event - Click on a numbered or prev/next page button.
   */
  goToPage(event) {
    const btn = event.target;
    if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;

    const page = Number.parseInt(btn.dataset.page ?? '', 10);
    if (!Number.isFinite(page) || page < 1 || page > this.#totalPages || page === this.#page) return;
    this.#fetchPage(page);
  }

  /**
   * @param {Event} event - Click on a card's read-more toggle.
   */
  toggleReadMore(event) {
    const toggle = event.target;
    if (!(toggle instanceof HTMLElement)) return;

    const bodyText = toggle.closest('.judgme-reviews__card-body')?.querySelector('.judgme-reviews__card-body-text');
    if (!bodyText) return;

    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    bodyText.classList.toggle('judgme-reviews__card-body-text--collapsed', expanded);
    toggle.setAttribute('aria-expanded', String(!expanded));
    toggle.textContent = expanded ? this.#i18n.readMore : this.#i18n.readLess;
  }

  #corpusSize() {
    return this.#tab === 'photos' ? this.#withPhotoCount : this.#allCount;
  }

  async #fetchCounts() {
    if (!this.#proxyUrl) return;
    try {
      const res = await fetch(`${this.#proxyUrl}/counts`);
      const data = await res.json();
      this.#allCount = Number(data?.all) || 0;
      this.#withPhotoCount = Number(data?.with_photo) || 0;
      // Only widens the estimate; #fetchPage's has_more signal still clamps it.
      const corpus = this.#corpusSize();
      if (corpus > 0) {
        const estimate = Math.max(1, Math.ceil(corpus / PAGE_SIZE));
        if (estimate > this.#totalPages) this.#totalPages = estimate;
        this.#renderPagination();
      }
      if (this.refs.photoCount) this.refs.photoCount.textContent = `(${this.#withPhotoCount})`;
    } catch (err) {
      console.warn('judgme-reviews counts fetch failed:', err);
    }
  }

  #readI18n() {
    const d = this.dataset;
    return {
      today: d.i18nToday || 'today',
      oneDay: d.i18nOneDay || '1 day ago',
      daysPrefix: d.i18nDaysPrefix || '',
      daysSuffix: d.i18nDaysSuffix || 'days ago',
      oneMonth: d.i18nOneMonth || '1 month ago',
      monthsPrefix: d.i18nMonthsPrefix || '',
      monthsSuffix: d.i18nMonthsSuffix || 'months ago',
      oneYear: d.i18nOneYear || '1 year ago',
      yearsPrefix: d.i18nYearsPrefix || '',
      yearsSuffix: d.i18nYearsSuffix || 'years ago',
      previousPage: d.i18nPreviousPage || 'Previous page',
      nextPage: d.i18nNextPage || 'Next page',
      readMore: d.i18nReadMore || 'Read more',
      readLess: d.i18nReadLess || 'Read less',
      loadFailed: d.i18nLoadFailed || 'Reviews could not be loaded.',
      retry: d.i18nRetry || 'Try again',
      anonymous: d.i18nAnonymous || 'Anonym',
      verified: d.verifiedLabel || 'Verifiziert',
    };
  }

  /**
   * @param {string} raw - ISO 8601 from the proxy, or Judge.me's widget format ("YYYY-MM-DD HH:MM UTC").
   */
  #timeAgo(raw) {
    if (!raw) return '';
    const clean = /T/.test(raw) ? raw : raw.replace(' UTC', '').replace(' ', 'T') + 'Z';
    const date = new Date(clean);
    if (Number.isNaN(date.getTime())) return '';
    const days = Math.ceil(Math.abs(Date.now() - date.getTime()) / 86400000);
    const i18n = this.#i18n;
    if (days === 0) return i18n.today;
    if (days === 1) return i18n.oneDay;
    if (days < 30) return `${i18n.daysPrefix} ${days} ${i18n.daysSuffix}`.trim();
    const months = Math.floor(days / 30);
    if (months === 1) return i18n.oneMonth;
    if (months < 12) return `${i18n.monthsPrefix} ${months} ${i18n.monthsSuffix}`.trim();
    const years = Math.floor(months / 12);
    if (years === 1) return i18n.oneYear;
    return `${i18n.yearsPrefix} ${years} ${i18n.yearsSuffix}`.trim();
  }

  /**
   * @param {number} page
   */
  async #fetchPage(page) {
    if (!this.#proxyUrl) return;

    // A tab or page change while a request is in flight must not be dropped: the
    // old request was built for the old tab and would render under the new one.
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.#showSkeletons();

    const qs = new URLSearchParams({ page: String(page), per_page: String(PAGE_SIZE) });
    // 'newest' is unfiltered like 'all': Judge.me already sorts newest-first and the proxy has no sort parameter.
    if (this.#tab === 'photos') qs.set('has_photo', '1');

    try {
      const res = await fetch(`${this.#proxyUrl}/reviews?${qs.toString()}`, { signal: request.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const reviews = Array.isArray(data?.reviews) ? data.reviews : [];

      this.#page = page;
      this.#reviews = reviews;
      if (!this.#featuredFilled && reviews.length > 0) {
        this.#featuredFilled = true;
        this.#fillFeaturedFallback(reviews[0]);
      }

      // /counts is the authoritative ceiling. has_more=false alone must not shrink it: hidden/spam
      // filtering upstream can legitimately return a short page. Only an empty page ends the data.
      if (data?.has_more === false && reviews.length === 0 && page > 1 && page < this.#totalPages) {
        this.#totalPages = Math.max(1, page - 1);
      } else if (data?.has_more === true && page >= this.#totalPages) {
        this.#totalPages = page + 1;
      }

      this.#render();
      this.#renderPagination();
      if (page > 1) this.refs.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      // Superseded by a newer request: that one owns the grid now.
      if (request.signal.aborted) return;
      console.error('judgme-reviews fetch failed:', err);
      this.#failedPage = page;
      this.#showError();
    } finally {
      if (this.#request === request) {
        this.#request = null;
        this.refs.grid.setAttribute('aria-busy', 'false');
      }
    }
  }

  /** Error-state button: loads the failed page again. */
  retry() {
    this.#fetchPage(this.#failedPage);
  }

  /** Replaces the skeletons with a message and a retry button instead of leaving them forever. */
  #showError() {
    const box = document.createElement('div');
    box.className = 'judgme-reviews__error';

    const message = document.createElement('p');
    message.className = 'judgme-reviews__error-text';
    message.textContent = this.#i18n.loadFailed;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'judgme-reviews__error-retry';
    button.setAttribute('on:click', '/retry');
    button.textContent = this.#i18n.retry;

    box.append(message, button);
    this.refs.grid.replaceChildren(box);
  }

  #showSkeletons() {
    const { grid } = this.refs;
    grid.setAttribute('aria-busy', 'true');
    // Only every fourth skeleton gets a photo block, so the masonry columns don't start level.
    const cards = Array.from({ length: PAGE_SIZE }, (_, i) => this.#buildSkeletonCard(i % 4 === 0));
    grid.replaceChildren(...cards);
  }

  /**
   * @param {boolean} withPhoto
   */
  #buildSkeletonCard(withPhoto) {
    const card = document.createElement('article');
    card.className = 'judgme-reviews__card judgme-reviews__card--skeleton';
    card.setAttribute('aria-hidden', 'true');
    if (withPhoto) {
      const photo = document.createElement('div');
      photo.className = 'judgme-reviews__skeleton-photo';
      card.appendChild(photo);
    }
    const lines = [
      'judgme-reviews__skeleton-line--stars',
      'judgme-reviews__skeleton-line--meta',
      'judgme-reviews__skeleton-line--title',
      'judgme-reviews__skeleton-line--body',
      'judgme-reviews__skeleton-line--body',
      'judgme-reviews__skeleton-line--body-short',
    ];
    for (const cls of lines) {
      const line = document.createElement('div');
      line.className = `judgme-reviews__skeleton-line ${cls}`;
      card.appendChild(line);
    }
    return card;
  }

  /**
   * First, last and the current page ±1, with an ellipsis for every gap.
   *
   * @returns {Array<number | '…'>}
   */
  #paginationRange() {
    const current = this.#page;
    const total = this.#totalPages;
    const window = 1;
    const pages = new Set([1, total]);
    for (let i = current - window; i <= current + window; i += 1) {
      if (i >= 1 && i <= total) pages.add(i);
    }
    const sorted = [...pages].sort((a, b) => a - b);
    /** @type {Array<number | '…'>} */
    const out = [];
    let prev = 0;
    for (const n of sorted) {
      if (n - prev > 1) out.push('…');
      out.push(n);
      prev = n;
    }
    return out;
  }

  #renderPagination() {
    const { pagination } = this.refs;
    if (this.#totalPages <= 1) {
      pagination.replaceChildren();
      return;
    }

    const fragment = document.createDocumentFragment();

    fragment.appendChild(
      this.#buildPageButton(this.#page - 1, '‹', {
        nav: true,
        disabled: this.#page <= 1,
        ariaLabel: this.#i18n.previousPage,
      })
    );

    for (const n of this.#paginationRange()) {
      if (n === '…') {
        const span = document.createElement('span');
        span.className = 'judgme-reviews__page judgme-reviews__page--ellipsis';
        span.textContent = '…';
        fragment.appendChild(span);
        continue;
      }
      fragment.appendChild(this.#buildPageButton(n, String(n), { active: n === this.#page }));
    }

    fragment.appendChild(
      this.#buildPageButton(this.#page + 1, '›', {
        nav: true,
        disabled: this.#page >= this.#totalPages,
        ariaLabel: this.#i18n.nextPage,
      })
    );

    pagination.replaceChildren(fragment);
  }

  /**
   * @param {number} page
   * @param {string} label
   * @param {{ nav?: boolean, disabled?: boolean, active?: boolean, ariaLabel?: string }} [options]
   */
  #buildPageButton(page, label, { nav = false, disabled = false, active = false, ariaLabel = '' } = {}) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'judgme-reviews__page';
    if (nav) btn.classList.add('judgme-reviews__page--nav');
    if (active) {
      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'page');
    }
    btn.dataset.page = String(page);
    btn.setAttribute('on:click', '/goToPage');
    btn.textContent = label;
    if (disabled) btn.disabled = true;
    if (ariaLabel) btn.setAttribute('aria-label', ariaLabel);
    return btn;
  }

  /**
   * @param {string | undefined} code - ISO country code.
   */
  #buildFlag(code) {
    if (!code) return null;
    const img = document.createElement('img');
    img.src = `https://flagcdn.com/w40/${code.toLowerCase()}.png`;
    img.alt = code;
    img.width = 20;
    img.height = 14;
    img.loading = 'lazy';
    img.className = 'judgme-reviews__flag-img';
    img.addEventListener(
      'error',
      () => {
        img.src = 'https://flagcdn.com/w40/un.png';
      },
      { once: true }
    );
    return img;
  }

  /**
   * @param {number} rating
   */
  #starsFor(rating) {
    const full = Math.floor(rating);
    const half = rating - full >= 0.25 && rating - full < 0.75 ? 1 : 0;
    const fullCount = rating - full >= 0.75 ? full + 1 : full;
    let html = STAR_FULL.repeat(fullCount);
    if (half) html += STAR_HALF;
    const shown = fullCount + half;
    html += STAR_EMPTY.repeat(Math.max(0, 5 - shown));
    return html;
  }

  /**
   * Fills an empty featured quote/author from the first review, unless the merchant set both.
   * The featured flag stays server-rendered (the visitor's country); the proxy returns no per-review country for it.
   *
   * @param {Record<string, any>} first
   */
  #fillFeaturedFallback(first) {
    const { featured, featuredQuote, featuredAuthor } = this.refs;
    if (!first || !featured || featured.dataset.static === 'true') return;

    if (featuredQuote && !featuredQuote.textContent?.trim() && first.body) {
      const template = featuredQuote.dataset.quoteTemplate || '%s';
      featuredQuote.textContent = template.replace('%s', first.body);
    }
    if (featuredAuthor && !featuredAuthor.textContent?.trim()) {
      featuredAuthor.textContent = first.author;
    }
  }

  #render() {
    this.refs.grid.replaceChildren(...this.#reviews.map((review) => this.#buildCard(review)));
  }

  /**
   * @param {Record<string, any>} review
   */
  #buildCard(review) {
    const card = document.createElement('article');
    card.className = 'judgme-reviews__card';

    const photoUrl = review.pictures?.[0];
    if (photoUrl) {
      const photo = document.createElement('div');
      photo.className = 'judgme-reviews__card-photo';
      const img = document.createElement('img');
      img.src = photoUrl;
      img.alt = '';
      img.loading = 'lazy';
      photo.appendChild(img);
      card.appendChild(photo);
    }

    const body = document.createElement('div');
    body.className = 'judgme-reviews__card-body';

    const top = document.createElement('div');
    top.className = 'judgme-reviews__card-top';
    const stars = document.createElement('span');
    stars.className = 'judgme-reviews__card-stars';
    stars.innerHTML = this.#starsFor(review.rating);
    top.appendChild(stars);
    const flag = this.#buildFlag(review.country);
    if (flag) {
      const flagWrap = document.createElement('span');
      flagWrap.className = 'judgme-reviews__card-flag';
      flagWrap.appendChild(flag);
      top.appendChild(flagWrap);
    }
    body.appendChild(top);

    const time = document.createElement('span');
    time.className = 'judgme-reviews__card-time';
    time.textContent = this.#timeAgo(review.created_at);
    body.appendChild(time);

    const authorRow = document.createElement('div');
    authorRow.className = 'judgme-reviews__card-author-row';
    const author = document.createElement('span');
    author.className = 'judgme-reviews__card-author';
    author.textContent = review.author || this.#i18n.anonymous;
    authorRow.appendChild(author);
    if (review.verified) {
      const verifiedWrapper = document.createElement('div');
      verifiedWrapper.className = 'judgme-reviews__card-verified-wrapper';
      const dot = document.createElement('span');
      dot.className = 'judgme-reviews__card-verified-dot';
      dot.setAttribute('aria-hidden', 'true');
      dot.innerHTML = VERIFIED_DOT;
      verifiedWrapper.appendChild(dot);
      const verified = document.createElement('span');
      verified.className = 'judgme-reviews__card-verified';
      verified.textContent = this.#i18n.verified;
      verifiedWrapper.appendChild(verified);
      authorRow.appendChild(verifiedWrapper);
    }
    body.appendChild(authorRow);

    if (review.title) {
      const title = document.createElement('p');
      title.className = 'judgme-reviews__card-title';
      title.textContent = review.title;
      body.appendChild(title);
    }

    if (review.body) {
      const isLong = review.body.length > READ_MORE_THRESHOLD;
      const bodyText = document.createElement('p');
      bodyText.className = 'judgme-reviews__card-body-text';
      if (isLong) bodyText.classList.add('judgme-reviews__card-body-text--collapsed');
      bodyText.textContent = review.body;
      body.appendChild(bodyText);

      if (isLong) {
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'judgme-reviews__card-read-more';
        toggle.textContent = this.#i18n.readMore;
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('on:click', '/toggleReadMore');
        body.appendChild(toggle);
      }
    }

    card.appendChild(body);
    return card;
  }
}

if (!customElements.get('judgme-reviews-component')) {
  customElements.define('judgme-reviews-component', JudgmeReviewsComponent);
}
